import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { U2000FormatDateTime } from '../../../u2000Core/tools/U2000DateTimeHelpers';
import { useU2000AuthContext } from '../../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import { FaHistory, FaUndo } from 'react-icons/fa';
import { NS, U3203ROLES } from '../../U3203';
import { useU3203AiMissionRevisionHandlers } from '../../hooks/useU3203AiMissionRevisionHandlers';
import type { U3203AiMissionFormRow, U3203AiMissionRevision, U3203MissionRevisionChangeType } from '../../U3203Service';
import U3203AiMissionRevisionDiff from './U3203AiMissionRevisionDiff';
import U3203AiMissionRestoreDlg from './U3203AiMissionRestoreDlg';

interface U3203AiMissionHistoryTabProps {
  missionId: string;
  systemScope: boolean;
  version: number;
  onMissionRestored: (row: U3203AiMissionFormRow) => void;
}

function changeTypeLabel(t: TFunction, changeType: U3203MissionRevisionChangeType): string {
  switch (changeType) {
    case 'create':
      return t('valChangeTypeCreate', { ns: NS });
    case 'update':
      return t('valChangeTypeUpdate', { ns: NS });
    case 'restore':
      return t('valChangeTypeRestore', { ns: NS });
    default:
      return changeType;
  }
}

function truncateComment(comment: string | null | undefined, max = 60): string {
  if (!comment?.trim()) return '—';
  const trimmed = comment.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
}

const U3203AiMissionHistoryTab: React.FC<U3203AiMissionHistoryTabProps> = ({
  missionId,
  systemScope,
  version,
  onMissionRestored,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const { revisions, loadingList, loadingRevision, restoring, loadRevisions, loadRevision, restoreRevision } =
    useU3203AiMissionRevisionHandlers();

  const [versionA, setVersionA] = useState<number | null>(null);
  const [versionB, setVersionB] = useState<number | null>(null);
  const [revisionA, setRevisionA] = useState<U3203AiMissionRevision | null>(null);
  const [revisionB, setRevisionB] = useState<U3203AiMissionRevision | null>(null);
  const [comparing, setComparing] = useState(false);
  const [restoreDlgOpen, setRestoreDlgOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<number | null>(null);

  const canRestore = !systemScope && hasRole(U3203ROLES.EDIT);

  const revisionOptions = useMemo(
    () => revisions.map((r) => ({ value: r.revisionNumber, label: `v${r.revisionNumber}` })),
    [revisions],
  );

  const initDefaultVersions = useCallback((list: typeof revisions) => {
    if (list.length === 0) {
      setVersionA(null);
      setVersionB(null);
      return;
    }
    const sorted = [...list].sort((a, b) => b.revisionNumber - a.revisionNumber);
    const latest = sorted[0]?.revisionNumber ?? null;
    const previous = sorted[1]?.revisionNumber ?? sorted[0]?.revisionNumber ?? null;
    setVersionA(previous);
    setVersionB(latest);
  }, []);

  useEffect(() => {
    void loadRevisions(missionId).then(initDefaultVersions);
  }, [missionId, loadRevisions, initDefaultVersions]);

  const handleCompare = async () => {
    if (versionA === null || versionB === null || versionA === versionB) {
      toast.error(t('msgSelectTwoRevisions', { ns: NS }), t('errorTitle', { ns: NS }));
      return;
    }
    setComparing(true);
    try {
      const [snapA, snapB] = await Promise.all([
        loadRevision(missionId, versionA),
        loadRevision(missionId, versionB),
      ]);
      setRevisionA(snapA);
      setRevisionB(snapB);
    } finally {
      setComparing(false);
    }
  };

  const handleRowClick = (revisionNumber: number) => {
    if (versionB === revisionNumber) {
      setVersionA(revisionNumber);
    } else {
      setVersionA(versionB);
      setVersionB(revisionNumber);
    }
  };

  const openRestoreDlg = (revisionNumber: number) => {
    setRestoreTarget(revisionNumber);
    setRestoreDlgOpen(true);
  };

  const handleRestoreConfirm = async (comment: string) => {
    if (restoreTarget === null) return;
    const restored = await restoreRevision(missionId, restoreTarget, version, comment || null);
    if (restored) {
      toast.success(t('msgRestoreSuccess', { ns: NS }), t('successTitle', { ns: NS }));
      setRestoreDlgOpen(false);
      setRestoreTarget(null);
      onMissionRestored(restored);
      initDefaultVersions(await loadRevisions(missionId));
    }
  };

  if (loadingList && revisions.length === 0) {
    return <p className="text-sm text-gray-600">{t('msgLoading', { ns: NS })}</p>;
  }

  if (revisions.length === 0) {
    return <p className="text-sm text-gray-600">{t('msgNoRevisions', { ns: NS })}</p>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-900">{t('lblRevisionHistory', { ns: NS })}</h3>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="overflow-auto rounded-md border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblRevisionNumber', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblChangeType', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblRevisionDate', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblRevisionAuthor', { ns: NS })}</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblRevisionComment', { ns: NS })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {revisions.map((rev) => (
                <tr
                  key={rev.revisionNumber}
                  className={`cursor-pointer hover:bg-blue-50 ${
                    versionA === rev.revisionNumber || versionB === rev.revisionNumber ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleRowClick(rev.revisionNumber)}
                >
                  <td className="px-3 py-2 font-medium">v{rev.revisionNumber}</td>
                  <td className="px-3 py-2">{changeTypeLabel(t, rev.changeType)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{U2000FormatDateTime(rev.createdAt)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{rev.createdBy.slice(0, 8)}…</td>
                  <td className="px-3 py-2 text-gray-600">{truncateComment(rev.comment)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="versionA" className="mb-1 block text-xs font-medium text-gray-700">
                {t('lblVersionA', { ns: NS })}
              </label>
              <select
                id="versionA"
                className="rounded-sm border border-gray-300 px-2 py-1 text-sm"
                value={versionA ?? ''}
                onChange={(e) => setVersionA(Number(e.target.value))}
              >
                {revisionOptions.map((opt) => (
                  <option key={`a-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="versionB" className="mb-1 block text-xs font-medium text-gray-700">
                {t('lblVersionB', { ns: NS })}
              </label>
              <select
                id="versionB"
                className="rounded-sm border border-gray-300 px-2 py-1 text-sm"
                value={versionB ?? ''}
                onChange={(e) => setVersionB(Number(e.target.value))}
              >
                {revisionOptions.map((opt) => (
                  <option key={`b-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <U2000ActionBtn
              label={t('lblCompareRevisions', { ns: NS })}
              icon={FaHistory}
              handleClick={() => void handleCompare()}
              disabled={comparing || loadingRevision}
            />
          </div>

          <U3203AiMissionRevisionDiff
            revisionA={revisionA}
            revisionB={revisionB}
            loading={comparing || loadingRevision}
            t={t}
            NS={NS}
          />

          {canRestore && versionB !== null ? (
            <U2000ActionBtn
              label={t('btnRestoreRevision', { revisionNumber: versionB, ns: NS })}
              icon={FaUndo}
              handleClick={() => openRestoreDlg(versionB)}
              disabled={restoring}
            />
          ) : null}
        </div>
      </div>

      <U3203AiMissionRestoreDlg
        isOpen={restoreDlgOpen}
        revisionNumber={restoreTarget}
        restoring={restoring}
        onClose={() => {
          setRestoreDlgOpen(false);
          setRestoreTarget(null);
        }}
        onConfirm={(comment) => void handleRestoreConfirm(comment)}
        t={t}
        NS={NS}
      />
    </div>
  );
};

export default U3203AiMissionHistoryTab;
